'use client';

import React, { useEffect, useState, useMemo } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Ruler, CheckCircle2, AlertTriangle, Plus, X } from 'lucide-react';

interface ConstraintConstructionMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  limits?: { maxActions?: number; [key: string]: any };
  payload: {
    attributes?: any[];
    components?: any[];
    requirements?: any[];
    _hasHints?: boolean;
  };
}

export default function ConstraintConstructionMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, limits, payload }: ConstraintConstructionMechanicProps) {
  const actualMechanicId = mechanicId || id || '';
  const [actionsLeft, setActionsLeft] = useState<number | undefined>(limits?.maxActions);

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={<Ruler className="w-5 h-5" />}
      iconLabel="Constraint Construction — Select components to satisfy requirements"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
      actionsLeft={actionsLeft}
    >
      {(submissionState, setAnswer) => (
        <ConstraintConstructionMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} actionsLeft={actionsLeft} setActionsLeft={setActionsLeft} />
      )}
    </BaseMechanicShell>
  );
}

function ConstraintConstructionMechanicInner({ payload, submissionState, setAnswer, actionsLeft, setActionsLeft }: any) {
  const attributes = useMemo(() => payload?.attributes || [], [payload?.attributes]);
  const components = useMemo(() => payload?.components || [], [payload?.components]);
  const requirements = useMemo(() => payload?.requirements || [], [payload?.requirements]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentMetrics, setCurrentMetrics] = useState<Record<string, number>>({});
  const [requirementsMet, setRequirementsMet] = useState<Record<string, boolean>>({});
  const [allMet, setAllMet] = useState(false);

  const isBlocked = actionsLeft !== undefined && actionsLeft <= 0 && submissionState === 'idle';

  useEffect(() => {
    // Calculate aggregated metrics based on selected components
    const metrics: Record<string, number> = {};
    attributes.forEach((attr: any) => {
      metrics[attr.id] = 0;
    });

    selectedIds.forEach(id => {
      const comp = components.find((c: any) => c.id === id);
      if (comp && comp.metrics) {
        Object.keys(comp.metrics).forEach(attrId => {
          metrics[attrId] = (metrics[attrId] || 0) + comp.metrics[attrId];
        });
      }
    });
    setCurrentMetrics(metrics);

    // Evaluate requirements
    const met: Record<string, boolean> = {};
    let all = true;
    requirements.forEach((req: any, idx: number) => {
      const val = metrics[req.attributeId] || 0;
      let isMet = false;
      switch (req.operator) {
        case '<': isMet = val < req.value; break;
        case '<=': isMet = val <= req.value; break;
        case '==': isMet = val === req.value; break;
        case '>=': isMet = val >= req.value; break;
        case '>': isMet = val > req.value; break;
      }
      met[idx] = isMet;
      if (!isMet) all = false;
    });

    setRequirementsMet(met);
    setAllMet(all);
    setAnswer(all ? selectedIds : null);

  }, [selectedIds, payload, attributes, components, requirements, setAnswer]);

  const toggleComponent = (id: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    if (isBlocked) return;
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(x => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
    if (setActionsLeft) {
      setActionsLeft((prev: number) => Math.max(0, prev - 1));
    }
  };

  const renderRequirement = (req: any, idx: number) => {
    const isMet = requirementsMet[idx];
    const val = currentMetrics[req.attributeId] || 0;
    const attr = attributes.find((a: any) => a.id === req.attributeId);
    
    // For progress bar: find max possible value for this attribute from all components if it's a "budget" style, 
    // or just use 2x the requirement value.
    const maxVal = Math.max(req.value * 1.5, val * 1.2, 10);
    const pct = Math.min(100, Math.max(0, (val / maxVal) * 100));
    const targetPct = Math.min(100, Math.max(0, (req.value / maxVal) * 100));

    let barColor = "bg-primary/50";
    if (isMet) {
      barColor = "bg-emerald-500/80";
    } else if (req.operator.includes("<") && val > req.value) {
      barColor = "bg-red-500/80";
    }

    return (
      <div key={idx} className="bg-muted/30 border border-border p-3 rounded-xl flex flex-col gap-2 relative overflow-hidden">
        <div className="flex justify-between items-center z-10 relative">
          <div className="text-xs font-semibold text-foreground/80 uppercase tracking-wide flex items-center gap-1.5">
            {isMet ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
            <span>{req.description || (attr?.label || req.attributeId)}</span>
          </div>
          <div className="flex items-baseline gap-0.5">
            <span className="text-xs font-mono font-bold text-muted-foreground">{req.operator}</span>
            <span className={`text-base font-mono font-bold ${isMet ? 'text-emerald-500' : (req.operator.includes("<") && val > req.value ? 'text-red-500' : 'text-foreground')}`}>{req.value}</span>
            <span className="text-xs text-muted-foreground/50 mx-0.5">/</span>
            <span className={`text-base font-mono font-bold ${isMet ? 'text-emerald-500' : (req.operator.includes("<") && val > req.value ? 'text-red-500' : 'text-foreground')}`}>{val}</span>
          </div>
        </div>
        
        {/* Progress Bar Container */}
        <div className="h-2 w-full bg-background rounded-full overflow-hidden relative z-10 shadow-inner">
           {/* Value Bar */}
           <div className={`absolute top-0 left-0 h-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
           {/* Target Marker */}
           <div className="absolute top-0 bottom-0 w-0.5 bg-foreground/50 z-20" style={{ left: `${targetPct}%` }} />
        </div>
      </div>
    );
  };

  const renderComponentCard = (comp: any) => {
    const isSelected = selectedIds.includes(comp.id);
    return (
      <button
        type="button"
        key={comp.id}
        onClick={() => toggleComponent(comp.id)}
        disabled={submissionState !== 'idle' || isBlocked}
        className={`relative flex flex-col text-left p-4 rounded-xl border-2 transition-all duration-300 ${
          isSelected 
            ? 'border-primary bg-primary/10 shadow-[0_0_15px_rgba(var(--primary),0.2)] scale-[1.02]' 
            : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
        }`}
      >
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center transition-colors">
          {isSelected ? (
            <div className="bg-primary text-primary-foreground rounded-full p-1"><X className="w-3 h-3" /></div>
          ) : (
            <div className="bg-muted text-muted-foreground rounded-full p-1"><Plus className="w-3 h-3" /></div>
          )}
        </div>
        
        <div className="font-bold text-sm mb-3 pr-8 text-foreground">{comp.content}</div>
        
        <div className="flex flex-wrap gap-2 mt-auto">
          {attributes.map((attr: any) => {
            const val = comp.metrics?.[attr.id];
            if (val === undefined || val === 0) return null;
            return (
              <div key={attr.id} className="bg-background/80 border border-border/50 px-2.5 py-1.5 rounded flex gap-2 items-center shadow-sm">
                <span className="text-xs font-semibold uppercase text-foreground/80">{attr.label}</span>
                <span className="text-sm font-mono font-bold text-primary">{val > 0 ? `+${val}` : val}</span>
              </div>
            );
          })}
        </div>
      </button>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-1">
      {/* Left panel: Requirements & Status */}
      <div className="w-full lg:w-1/3 flex flex-col gap-4">
        <div className="bg-card border border-border p-5 rounded-2xl shadow-sm">
          <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground mb-4">
            Constraints
          </h3>
          <div className="flex flex-col gap-3">
             {requirements.map(renderRequirement)}
          </div>
          
          <div className={`mt-6 p-4 rounded-xl flex items-center justify-between border transition-colors ${allMet ? 'bg-emerald-500/10 border-emerald-500/30' : isBlocked ? 'bg-destructive/10 border-destructive/30' : 'bg-muted/50 border-transparent'}`}>
            <div className="text-sm font-bold">
              {allMet ? <span className="text-emerald-500">All constraints met!</span> : isBlocked ? <span className="text-destructive">No actions left. Submit or refresh.</span> : <span className="text-muted-foreground">Select components to build a solution.</span>}
            </div>
            {allMet && <CheckCircle2 className="w-5 h-5 text-emerald-500 animate-pulse" />}
          </div>
        </div>
      </div>
      
      {/* Right panel: Component Inventory */}
      <div className="w-full lg:w-2/3">
        <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground mb-4 pl-1">Components</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {components.map(renderComponentCard)}
        </div>
      </div>
    </div>
  );
}
